import { Component, inject, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { faDownload, faUpload, faEdit } from '@fortawesome/free-solid-svg-icons';
import { MmssDocumentationSettings } from '@/table-settings/mmss-documentation-settings';
import { MmssDocumentationDataSource } from '@components/new-table/data-sources/mmss-documentation-data-source';
import { DocumentationMmssService } from '@services/documentation-mmss.service';
import { ToastrService } from 'ngx-toastr';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MmssDocumentationModel } from '@/models/tables/mmss-documentation.model';
import { LoggerService } from '@services/logger.service';
import { DialogPopUpComponent } from '@components/dialog-pop-up/dialog-pop-up.component';
import { saveAs } from 'file-saver';
import { CustomButtonInterface } from '@components/new-table/custom-button-interface';
import { DialogUploadDocumentComponent } from './dialog-upload-document/dialog-upload-document.component';
import { BrandModel } from '@/models/tables/brand.model';
import { BiggerDiscountService } from '@services/bigger-discount.service';

@Component({
  selector: 'app-documentation-mmss',
  templateUrl: './documentation-mmss.component.html',
  styleUrls: ['./documentation-mmss.component.scss'],
})
export class DocumentationMmssComponent implements OnInit {
  reloadTrigger$ = new Subject<any>();
  displayedColumns = MmssDocumentationSettings.getColumnsSetting(this.documentationMmssService);
  dataSource = new MmssDocumentationDataSource(this.documentationMmssService);
  toastr = inject(ToastrService);
  protected readonly faDownload = faDownload;
  protected readonly faUpload = faUpload;
  protected readonly faEdit = faEdit;
  brandIdSelected: number;
  triggerSelectionBrand$ = new Subject<any>();
  triggerUpdateComponentSelectBrand$ = new Subject<any>();
  brandIdDefault: number;
  brands: BrandModel[];
  brandsMeta = {
    label: 'Seleziona il brand',
    options: [],
    default: undefined as number | undefined,
  };

  customButtons: CustomButtonInterface[] = [
    {
      btnCustomFaIcon: this.faDownload,
      btnCustomShowIcon: true,
      btnCustomToolTip: 'Scarica documento',
      btnCustomClickEvent: (row: MmssDocumentationModel) => this.downloadDocument(row),
    },
    {
      btnCustomFaIcon: this.faEdit,
      btnCustomShowIcon: true,
      btnCustomToolTip: 'Modifica documento',
      btnCustomClickEvent: (row: MmssDocumentationModel) => this.editDocument(row),
    },
  ];

  constructor(
    private documentationMmssService: DocumentationMmssService,
    private biggerDiscountService: BiggerDiscountService,
    private dialog: MatDialog,
  ) {
    this.brandIdDefault = 3;

    // Usa getAllBrands() come in table-ms per avere il formato corretto
    this.biggerDiscountService.getAllBrands().subscribe((brands) => {
      this.brands = brands;
      this.brandsMeta.options = brands.map((brand) => ({
        key: brand.id,
        value: brand.descrizioneMarca,
      }));
      this.brandsMeta.default = this.brandIdDefault;

      this.triggerUpdateComponentSelectBrand$.next();
    });

    this.triggerSelectionBrand$.subscribe((brandId: number) => {
      this.brandIdSelected = brandId;
    });
  }

  ngOnInit(): void {
    // Component initialization
  }

  downloadDocument(document: MmssDocumentationModel) {
    this.documentationMmssService.downloadDocument(document.id).subscribe({
      next: (blob: Blob) => {
        const fileName = document.file?.name || `documento-${document.id}.pdf`;
        saveAs(blob, fileName);
        this.toastr.success('Documento scaricato con successo!');
      },
      error: (error) => {
        LoggerService.error('downloadDocument failed!');
        LoggerService.error(error);
        this.toastr.error('Errore durante il download del documento');
      },
    });
  }

  btnDeleteClick(document: MmssDocumentationModel) {
    let dialogRef = this.dialog.open(DialogPopUpComponent, {
      width: 'auto',
      panelClass: 'custom-modalbox',
    });

    this.setupPopup(dialogRef);

    dialogRef.afterClosed().subscribe((deleteDocument) => {
      if (deleteDocument) {
        this.documentationMmssService.deleteDocument(document.id).subscribe({
          next: () => {
            this.reloadTrigger$.next(1);
            this.toastr.success('Documento cancellato correttamente!');
          },
          error: (error) => {
            this.toastr.error('Errore durante la cancellazione del documento.');
            LoggerService.error('deleteDocument failed!');
            LoggerService.error(error);
          },
        });
      }
    });
  }

  private setupPopup(dialogRef: MatDialogRef<DialogPopUpComponent>) {
    dialogRef.componentInstance.title = 'Confermi di voler cancellare questo documento?';
    dialogRef.componentInstance.content =
      'Attenzione: una volta cancellato il documento non sarà più possibile recuperarlo.';
    dialogRef.componentInstance.firstCTA = 'Si cancella';
    dialogRef.componentInstance.secondCTA = 'Annulla operazione';
    dialogRef.componentInstance.closeResultFirst = true;
    dialogRef.componentInstance.closeResultSecond = false;
  }

  uploadDocument() {
    const modalTrigger$ = new Subject<any>();

    modalTrigger$.subscribe({
      next: () => {
        this.reloadTrigger$.next(1);
      },
    });

    const dialogRef = this.dialog.open(DialogUploadDocumentComponent, {
      width: 'auto',
      panelClass: 'custom-modalbox',
      data: {
        modalTrigger$: modalTrigger$,
        isEditMode: false,
      },
    });
  }

  editDocument(document: MmssDocumentationModel) {
    const modalTrigger$ = new Subject<any>();

    modalTrigger$.subscribe({
      next: () => {
        this.reloadTrigger$.next(1);
      },
    });

    const dialogRef = this.dialog.open(DialogUploadDocumentComponent, {
      width: 'auto',
      panelClass: 'custom-modalbox',
      data: {
        modalTrigger$: modalTrigger$,
        isEditMode: true,
        document: document,
      },
    });
  }
}
